import React from "react";
import clsx from "clsx";

import { ReactComponent as IconUser } from "../../../assets/images/AnswerList/icon_user.svg";
import { ReactComponent as IconPencil } from "../../../assets/images/AnswerList/icon_pencil.svg";
import { ReactComponent as IconTrash } from "../../../assets/images/AnswerList/icon_trash.svg";
import { IStudent, IAnswerComment } from "../../../state";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { getConfirmModalMessage } from "../../../libs/domUtils";

import style from "./FormReply.module.scss";

type Props = {
  comment: IAnswerComment;
  currentUser?: IStudent;
  currentStudentId?: number;
  answerId: number;
  name_opened: boolean;
  calling?: boolean;
  className?: string;
  iconWrapperClassName?: string;
  disableCancelConfirm?: boolean;
  onDeleteReply: (answerId: number, commentId: number) => void;
  onSendComment: (text: string, answerId: number, commentId?: number) => void;
};

const FormReply: React.FC<Props> = ({
  comment,
  onDeleteReply,
  onSendComment,
  currentUser,
  currentStudentId,
  answerId,
  name_opened,
  calling,
  className,
  iconWrapperClassName,
  disableCancelConfirm,
}) => {
  const [showForm, setShowForm] = React.useState<boolean>(false);
  const [content, setContent] = React.useState<string>(comment.content);
  const [openModal, setOpenModal] = React.useState<{
    state: boolean;
    type: "delete" | "edit";
    answerId: number;
    commentId: number;
  }>({
    state: false,
    type: "delete",
    answerId: 0,
    commentId: 0,
  });

  const updateReply = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSendComment(content, answerId, comment.id);
    setShowForm(!showForm);
  };

  const modalMessage = getConfirmModalMessage({
    type: openModal.type,
  });

  const handleModalConfirm = () => {
    if (openModal.type === "delete") {
      onDeleteReply(openModal.answerId, openModal.commentId);
    }

    if (openModal.type === "edit") {
      setShowForm(false);
      setContent(comment.content);
    }

    setOpenModal({
      state: false,
      type: "delete",
      answerId: 0,
      commentId: 0,
    });
  };

  const isStudentComment = !!(comment.student_id || comment.student);

  const isCurrentUser = currentStudentId
    ? comment.student_id === currentStudentId
    : !!(currentUser && currentUser.id === comment.student?.id);

  return (
    <div className={clsx(style.body, className)}>
      {iconWrapperClassName ? (
        <span className={iconWrapperClassName}>
          <IconUser
            className={clsx(
              style.userIcon,
              isCurrentUser && style.isCurrentUser,
            )}
          />
        </span>
      ) : (
        <IconUser
          className={clsx(style.userIcon, isCurrentUser && style.isCurrentUser)}
        />
      )}
      <div className={style.wrapper}>
        {name_opened ? (
          <div className={style.header}>
            {isStudentComment ? (
              <p
                className={clsx(
                  style.name,
                  isCurrentUser && style.isCurrentUser,
                )}
              >
                {currentStudentId && !isCurrentUser
                  ? "******"
                  : comment.student
                  ? `${comment.student.last_name} ${comment.student.first_name}`
                  : comment.name}
                {isCurrentUser && " (あなたのコメント)"}
              </p>
            ) : (
              <p className={style.name}>
                {comment.teacher
                  ? `${comment.teacher.last_name} ${comment.teacher.first_name} 先生`
                  : `${comment.name} 先生`}
              </p>
            )}
          </div>
        ) : isCurrentUser ? (
          <div className={style.header}>
            <p className={clsx(style.name, style.isCurrentUser)}>
              (あなたのコメント)
            </p>
          </div>
        ) : null}
        <div className={style.content}>
          {showForm ? (
            <form onSubmit={updateReply} className={style.form}>
              <textarea
                className={style.textarea}
                rows={3}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="コメントを入力してください。"
              />
              <div className={style.buttonGroup}>
                <button
                  type="button"
                  className={style.cancelButton}
                  onClick={() => {
                    if (!disableCancelConfirm && content !== comment.content) {
                      setOpenModal({
                        state: true,
                        type: "edit",
                        answerId,
                        commentId: comment.id,
                      });
                    } else {
                      setShowForm(false);
                      setContent(comment.content);
                    }
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className={style.saveButton}
                  disabled={calling || !content || content === comment.content}
                >
                  保存する
                </button>
              </div>
            </form>
          ) : (
            <div className={style.comment}>
              <p className={style.text}>
                {!comment.hidden_by
                  ? comment.content
                  : `${comment.hidden_by.last_name} ${comment.hidden_by.first_name}先生が、非表示にしました。`}
              </p>
              {isCurrentUser && (
                <div className={style.actions}>
                  <IconPencil
                    onClick={() => setShowForm(!showForm)}
                    className={clsx(calling && style.disabled)}
                  />
                  <IconTrash
                    className={clsx(calling && style.disabled)}
                    onClick={() => {
                      setOpenModal({
                        state: true,
                        type: "delete",
                        answerId,
                        commentId: comment.id,
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {openModal.state && (
        <ModalDeleteConfirm
          title={modalMessage}
          onConfirm={() => handleModalConfirm()}
          onClose={() =>
            setOpenModal({
              state: false,
              type: "delete",
              answerId: 0,
              commentId: 0,
            })
          }
        />
      )}
    </div>
  );
};

export default FormReply;
